import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import RefreshIcon from '@mui/icons-material/RefreshOutlined';
import { HttpStatusCode, isAxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useStoreSelector } from 'v3/data/store';
import { showErrorDialog } from 'v3/data/store/error/actions';
import {
  clearOnlineContractingInfo,
  setContractingKey,
  setContractingOnlineState,
} from 'v3/data/store/onlineContracting/actions';
import { OnlineContractingStateEnum } from 'v3/data/store/onlineContracting/enums';
import { OnlineContractingStateMapping } from 'v3/data/store/onlineContracting/mapping';

import { useBreakpoints } from 'v3/hooks/useBreakpoints';

import { IContractingDetailsParams } from 'v3/pages/Contracting/Details/Cards/types';
import { OnlineFinancingInformation } from 'v3/pages/OnlineContracting/Details/Cards/FinancingInformation';
import { OnlineHistoryInformation } from 'v3/pages/OnlineContracting/Details/Cards/HistoryInformation';
import { PersonalInformationInApp } from 'v3/pages/OnlineContracting/Details/Cards/PersonalInformation';
import { OnlineTransactionInformation } from 'v3/pages/OnlineContracting/Details/Cards/TransactionInformation';

import { GO_BACK } from 'v3/routes/enum';
import * as onlineApi from 'v3/services/online/api';

import { CircularButton } from 'v3/shared/components/Button/CircularButton';
import { FlexButton } from 'v3/shared/components/Button/FlexButton';
import { Status } from 'v3/shared/components/Status';
import { OnlineCommonTemplate } from 'v3/shared/templates/OnlineCommomTemplate';
import { HeadlineMedium } from 'v3/shared/typography/HeadlineMedium';

import {
  setCancelationModalOpen,
  setClearCancellationState,
  setCurrentCancellationState,
} from 'v3/data/store/cancellationNavigation/actions';
import { CancellationAllowedEnum, CancellationNavigationEnum } from 'v3/data/store/cancellationNavigation/enum';
import {
  CancellationStateMapping,
  CancellationStateMappingDisableToCancel,
} from 'v3/data/store/cancellationNavigation/mapping';
import { getStorePermissions } from 'v3/data/store/stores/actions';
import { CancellationFlowModal } from 'v3/pages/OnlineContracting/Cancellation';
import { useFetchContractingDetails } from 'v3/pages/OnlineContracting/Details/get-contracting-details-hook';
import {
  AlignMobileButton,
  AlignStatusAndProof,
  ButtonsWrapper,
  Container,
  Content,
  Header,
  MainTitle,
} from './styles';
import { PERMISSIONS } from 'v3/data/store/stores/constants';
import { StoreProfileEnum } from 'v3/data/store/stores/enums';

export const OnlineContractingDetailsPage = () => {
  const navigate = useNavigate();
  const { contractingKey } = useParams<Readonly<IContractingDetailsParams>>() as IContractingDetailsParams;
  const { contractingState, paymentDetails } = useStoreSelector((store) => store.onlineContracting);
  const { currentCancelationState } = useStoreSelector((store) => store.cancellationNavigation);
  const { isDesktop } = useBreakpoints();
  const [refundButtonActive, setRefundButtonActive] = useState(false);
  const [cancelButtonActive, setCancelButtonActive] = useState(false);
  const group = useStoreSelector((store) => store.store.storeLogged.group);
  const { fetchContractingDetails } = useFetchContractingDetails();

  const permissions = useMemo(() => {
    return PERMISSIONS[group ?? StoreProfileEnum.EMPTY];
  }, [group]);

  const { isMaster } = permissions;

  const cancellationStateTitle = CancellationStateMapping[currentCancelationState ?? '']?.title;
  const contractingStateTitle = OnlineContractingStateMapping[contractingState]?.title;
  const statusTitle = cancellationStateTitle ?? contractingStateTitle;

  const fetchCancellationStatus = useCallback(async () => {
    try {
      const response = await onlineApi.getCancellationStatus(contractingKey);
      setCurrentCancellationState(response.data.status);
    } catch (error: any) {
      setClearCancellationState();
      if (error?.response?.status !== 404) {
        showErrorDialog('Erro ao obter dados de cancelamento');
      }
    }
  }, [contractingKey]);

  const handleButtonActivation = () => {
    if (!group) return;

    const shouldAllowCancellation = CancellationAllowedEnum.includes(contractingState);
    const hasImei = Boolean(paymentDetails.device?.imei);
    const cancellationAllowed = !CancellationStateMappingDisableToCancel.find(
      (status: CancellationNavigationEnum) => status === currentCancelationState,
    );

    if (!shouldAllowCancellation || isMaster) {
      setCancelButtonActive(false);
      setRefundButtonActive(false);
      return
    };

    const isEnabledCancelButton = hasImei && cancellationAllowed;
    const isEnabledRefundButton = !hasImei && cancellationAllowed;

    setCancelButtonActive(isEnabledCancelButton);
    setRefundButtonActive(isEnabledRefundButton);
  };
  const updateStatus = useCallback(async () => {
    try {
      const response = await onlineApi.getContractingState(contractingKey);
      setContractingOnlineState(response.data.status);
    } catch (error) {
      if (isAxiosError(error)) {
        const { status } = error.response || {};
        if (status === HttpStatusCode.Locked || status === HttpStatusCode.Gone) {
          setContractingOnlineState(OnlineContractingStateEnum.ExpiredKey);
        }
      } else {
        showErrorDialog('Erro ao obter status da contratação', error);
      }
    }
  }, [contractingKey]);

  const refreshStatus = () => {
    fetchCancellationStatus();
    updateStatus();
  };

  const cancelationFlow = () => {
    handleCancelationFlow(CancellationNavigationEnum.CANCELLATION_NOT_STARTED);
  };

  const refundFlow = () => {
    handleCancelationFlow(CancellationNavigationEnum.REFUND_STARTED);
  };

  const handleCancelationFlow = (cancelationState: CancellationNavigationEnum) => {
    if (!currentCancelationState || currentCancelationState === CancellationNavigationEnum.CANCELLATION_REFUSED) {
      setCurrentCancellationState(cancelationState);
    } else {
      setCurrentCancellationState(currentCancelationState);
    }
    setCancelationModalOpen(true);
  };

  useEffect(() => {
    fetchContractingDetails(contractingKey);
  }, [contractingState, currentCancelationState]);

  useEffect(() => {
    handleButtonActivation();
  }, [contractingState, currentCancelationState, paymentDetails.device.imei, isMaster, handleButtonActivation]);

  useEffect(() => {
    clearOnlineContractingInfo();
    setContractingKey(contractingKey);
    updateStatus();
    fetchCancellationStatus();
  }, [updateStatus, fetchCancellationStatus]);

  return (
    <OnlineCommonTemplate>
      <Container>
        <Content>
          <Header>
            <MainTitle>
              <ArrowBackIcon className="pointer" fontSize="large" onClick={() => navigate(GO_BACK)} />
              <HeadlineMedium marginRight="auto">Detalhes da proposta</HeadlineMedium>
            </MainTitle>
            <AlignStatusAndProof>
              {!isDesktop ? (
                <AlignMobileButton>
                  <CircularButton startIcon={ArrowBackIcon} handleClick={refundFlow} disabled={!refundButtonActive} />
                  <CircularButton
                    startIcon={HighlightOffIcon}
                    handleClick={cancelationFlow}
                    disabled={!cancelButtonActive}
                  />
                  <CircularButton startIcon={RefreshIcon} handleClick={refreshStatus} />
                </AlignMobileButton>
              ) : (
                <ButtonsWrapper>
                  <FlexButton
                    size="small"
                    text="Estorno"
                    startIcon={ArrowBackIcon}
                    handleClick={refundFlow}
                    disabled={!refundButtonActive}
                  />
                  <FlexButton
                    size="small"
                    text="Cancelar"
                    startIcon={HighlightOffIcon}
                    handleClick={cancelationFlow}
                    disabled={!cancelButtonActive}
                  />
                  <FlexButton size="small" text="Atualizar status" startIcon={RefreshIcon} handleClick={updateStatus} />
                </ButtonsWrapper>
              )}
              <Status title={statusTitle} />
            </AlignStatusAndProof>
          </Header>
          <OnlineTransactionInformation />
          <PersonalInformationInApp />
          <OnlineFinancingInformation />
          <OnlineHistoryInformation />
        </Content>
        <CancellationFlowModal />
      </Container>
    </OnlineCommonTemplate>
  );
};
